import type { GroupDetailsInterface, ProgramQuestionInterface } from '../../components/feedback/SessionFeedback';
import type { SessionInterface } from '../../components/feedback/PreviousSessionMenu';
import { baseApi } from './baseApi';

export interface FeedbackService {
	id: number;
	service_name: string;
	label: string;
	value: number;
}

export interface FeedbackUserDetails {
	id: number;
	full_name: string;
	profile_photo_url: string;
	programs: { id: number; name: string; description: string }[];
	user_type: string;
}

export interface SessionFeedbackAnswers {
	mentee: { submitted: string; feedback: SessionFeedbackAnswer[] }[];
	mentor: { submitted: string; feedback: SessionFeedbackAnswer[] }[];
}

export interface SessionFeedbackAnswer {
	question_id: number;
	question_category: string;
	text: string | string[] | null;
	value: number | null;
}

export const feedbackApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getFeedbackUserDetails: build.query<FeedbackUserDetails, number>({
			query: (userId) => `/api/rails/api/v1/users/${userId}`,
			providesTags: (_user, _error, userId) => [{ type: 'Users', id: userId }],
		}),
		getFeedbackServices: build.query<FeedbackService[], void>({
			query: () => '/api/rails/api/v1/services',
			providesTags: [{ type: 'Feedback', id: 'SERVICES' }],
		}),
		getProgramGroups: build.query<GroupDetailsInterface[], number>({
			query: (programId) => `/api/rails/api/portal/v1/mentorship_ratings/main?program_id=${programId}`,
			providesTags: (_groups, _error, programId) => [{ type: 'Program', id: programId }],
		}),
		getProgramQuestions: build.query<ProgramQuestionInterface[], { programId: number; userType: string }>({
			query: ({ programId, userType }) =>
				`/api/rails/api/portal/v1/program/review_questions?program_id=${programId}&user_type=${userType}`,
			providesTags: (_questions, _error, { programId, userType }) => [
				{ type: 'Program', id: `${programId}-${userType}` },
			],
		}),
		getProgramSessions: build.query<SessionInterface, number>({
			query: (userId) => `/api/rails/api/portal/v1/participant_bookings?user_id=${userId}`,
			providesTags: (_sessions, _error, userId) => [{ type: 'Session', id: userId }],
		}),
		getSessionFeedbackAnswers: build.query<SessionFeedbackAnswers, number>({
			query: (bookingId) => `/api/rails/api/portal/v1/mentorship_ratings/booking_reviews?booking_id=${bookingId}`,
			providesTags: (_answers, _error, bookingId) => [{ type: 'Feedback', id: bookingId }],
		}),
		submitOfflineFeedback: build.mutation<void, FormData>({
			query: (body) => ({ url: '/api/rails/api/portal/v1/feedback_management/offline_submit', method: 'POST', body }),
			invalidatesTags: [{ type: 'Feedback', id: 'SERVICES' }],
		}),
		submitSessionFeedback: build.mutation<void, { bookingId: number; formData: FormData }>({
			query: ({ bookingId, formData }) => ({ url: `/api/rails/api/v1/bookings/${bookingId}/reviews`, method: 'POST', body: formData }),
			invalidatesTags: (_result, _error, { bookingId }) => [
				{ type: 'Feedback', id: bookingId },
				{ type: 'Session', id: 'LIST' },
			],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetFeedbackServicesQuery,
	useGetFeedbackUserDetailsQuery,
	useLazyGetFeedbackUserDetailsQuery,
	useGetProgramGroupsQuery,
	useLazyGetProgramGroupsQuery,
	useGetProgramQuestionsQuery,
	useLazyGetProgramQuestionsQuery,
	useGetProgramSessionsQuery,
	useLazyGetSessionFeedbackAnswersQuery,
	useGetSessionFeedbackAnswersQuery,
	useSubmitOfflineFeedbackMutation,
	useSubmitSessionFeedbackMutation,
} = feedbackApi;
